import { AnyExtension, Editor, Node as TiptapNode } from '@tiptap/core';
import { Fragment, Schema } from '@tiptap/pm/model';
import Document from '@tiptap/extension-document';
import Text from '@tiptap/extension-text';
import Paragraph from '@tiptap/extension-paragraph';
import HardBreak from '@tiptap/extension-hard-break';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Underline from '@tiptap/extension-underline';
import Strike from '@tiptap/extension-strike';
import TextStyle from '@tiptap/extension-text-style';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import ListItem from '@tiptap/extension-list-item';
import HorizontalRule from '@tiptap/extension-horizontal-rule';
import History from '@tiptap/extension-history';
import { MergeTagNode } from './MergeTagNode';
import { RAW_BLOCK_TAGS, RAW_ISLAND_SELECTOR, RawBlock, RawInline } from './RawNodes';

// [attribute name, css property], in the order the legacy toolbar wrote them.
const TEXT_STYLE_PROPS = [
	['color', 'color'],
	['backgroundColor', 'background-color'],
	['fontSize', 'font-size'],
	['fontFamily', 'font-family'],
	['textTransform', 'text-transform'],
	['whiteSpace', 'white-space'],
	['letterSpacing', 'letter-spacing'],
	['lineHeight', 'line-height'],
	['textDecoration', 'text-decoration'],
] as const;

export const DYNAMIC_COLOR_ATTR = 'data-bay-color-var';
export const DYNAMIC_BACKGROUND_COLOR_ATTR = 'data-bay-background-color-var';

// Split a style attribute at the `;` that end a declaration: not inside (...) (a
// url(data:image/png;base64,...) carries its own `;`) and not inside quotes.
export function splitDeclarations(style: string) {
	const parts: string[] = [];
	let depth = 0;
	let quote: string | null = null;
	let current = '';
	for (const ch of style) {
		if (quote) {
			if (ch === quote) quote = null;
		} else if (ch === '"' || ch === "'") {
			quote = ch;
		} else if (ch === '(') {
			depth += 1;
		} else if (ch === ')') {
			depth = Math.max(0, depth - 1);
		} else if (ch === ';' && depth === 0) {
			parts.push(current);
			current = '';
			continue;
		}
		current += ch;
	}
	parts.push(current);
	return parts;
}

// Read the declared value from the style attribute instead of element.style so a
// stored hex is not normalised to rgb() on the way in.
function readStyleProp(element: HTMLElement, cssProp: string) {
	const style = element.getAttribute('style') || '';
	const declaration = splitDeclarations(style)
		.map(part => part.trim())
		.find(part => part.toLowerCase().startsWith(`${cssProp}:`));
	if (!declaration) return null;
	const value = declaration.slice(declaration.indexOf(':') + 1).trim();
	return value || null;
}

const FONT_SIZE_KEYWORDS: Record<string, string> = {
	'xx-small': '9px',
	'x-small': '10px',
	small: '13px',
	medium: '16px',
	large: '18px',
	'x-large': '24px',
	'xx-large': '32px',
	'xxx-large': '48px',
};

// The toolbar treats font-size as a bare px number; a keyword would render "mediumpx".
function normalizeFontSize(value: string | null) {
	if (!value) return value;
	// Relative keywords have no px value; the block default applies.
	if (/^(smaller|larger)$/i.test(value)) return null;
	return FONT_SIZE_KEYWORDS[value.toLowerCase()] || value;
}

// Inline elements a styled span can sit inside without breaking inheritance
// (never a div/block, never a raw island).
const INHERIT_THROUGH = new Set(['SPAN', 'B', 'STRONG', 'I', 'EM', 'U', 'A', 'S', 'STRIKE', 'DEL', 'FONT']);

// The mark is a single textStyle, so ProseMirror replaces an outer span's mark with
// the inner one; whatever the inner span leaves unset has to come from the ancestors.
// The chain holds the spans and the legacy mark tags between them: a mark tag keeps
// its own style on output, so a property it declares blocks the spans beyond it.
function inheritanceChain(element: HTMLElement) {
	const chain: HTMLElement[] = [];
	let parent = element.parentElement;
	// A link carries its own colours; a span outside it never passes styles in.
	while (
		parent &&
		parent.tagName !== 'A' &&
		INHERIT_THROUGH.has(parent.tagName) &&
		!parent.matches(RAW_ISLAND_SELECTOR)
	) {
		chain.push(parent);
		parent = parent.parentElement;
	}
	return chain;
}

// `inherit`/`unset` on the span itself means "take the ancestor's", not a value.
const DEFERS_TO_ANCESTOR = /^(inherit|unset)$/i;

function readInheritedStyleProp(element: HTMLElement, cssProp: string) {
	const own = readStyleProp(element, cssProp);
	if (own && !DEFERS_TO_ANCESTOR.test(own)) return own;
	for (const ancestor of inheritanceChain(element)) {
		const value = readStyleProp(ancestor, cssProp);
		if (!value || DEFERS_TO_ANCESTOR.test(value)) continue;
		// The mark tag renders its own value around this span.
		return ancestor.tagName === 'SPAN' ? value : null;
	}
	// Nothing to defer to: keep the span's own keyword so the block saves unchanged.
	return own;
}

// A dynamic colour attribute travels with the colour it drives: inherited only from
// the span the colour itself comes from, never over a colour the span sets.
function readInheritedColorVar(element: HTMLElement, cssProp: string, attr: string) {
	for (const node of [element, ...inheritanceChain(element)]) {
		const declares = node.hasAttribute(attr) || readStyleProp(node, cssProp);
		if (!declares) continue;
		return node === element || node.tagName === 'SPAN' ? node.getAttribute(attr) : null;
	}
	return null;
}

// ProseMirror's serializer writes a `style` spec attribute through
// `dom.style.cssText`, which every engine normalises (hex -> rgb(), and jsdom even
// drops `inherit`). A stored colour must come back byte for byte, so these marks
// build their element and set the attribute verbatim instead of returning a spec.
function elementWithRawAttributes(tag: string, attrs: Record<string, any>) {
	const el = document.createElement(tag);
	Object.entries(attrs).forEach(([name, value]) => {
		if (value === null || value === undefined || value === '') return;
		el.setAttribute(name, String(value));
	});
	// Content renders inside the element itself (the `0` hole of a spec array).
	return { dom: el, contentDOM: el };
}

// Legacy mark elements keep their own style/class (read only from the mark's own
// tags, never from a <span style="font-weight: bold"> another rule matched).
function markAttributes(tags: string[]) {
	const attributes: Record<string, any> = {};
	['style', 'class'].forEach(name => {
		attributes[name] = {
			default: null,
			parseHTML: (element: HTMLElement) =>
				tags.includes(element.tagName.toLowerCase()) ? element.getAttribute(name) : null,
			rendered: false,
		};
	});
	return attributes;
}

function markElement(tag: string, mark: { attrs: Record<string, any> }, base: Record<string, any>) {
	return elementWithRawAttributes(tag, {
		...base,
		style: mark.attrs.style,
		class: mark.attrs.class,
	});
}

// Legacy tag names so an untouched block re-serialises without a diff.
const LegacyBold = Bold.extend({
	addAttributes() {
		return { ...this.parent?.(), ...markAttributes(['b', 'strong']) };
	},
	renderHTML({ mark }) {
		return markElement('b', mark, this.options.HTMLAttributes);
	},
});

const LegacyItalic = Italic.extend({
	addAttributes() {
		return { ...this.parent?.(), ...markAttributes(['i', 'em']) };
	},
	renderHTML({ mark }) {
		return markElement('i', mark, this.options.HTMLAttributes);
	},
});

// Tag-only: text-decoration belongs to InlineTextStyle, not a second strike mark.
const LegacyStrike = Strike.extend({
	addAttributes() {
		return { ...this.parent?.(), ...markAttributes(['s', 'del', 'strike']) };
	},
	parseHTML() {
		return [{ tag: 's' }, { tag: 'del' }, { tag: 'strike' }];
	},
	renderHTML({ mark }) {
		return markElement('strike', mark, this.options.HTMLAttributes);
	},
});

// style/class/id on block elements are kept verbatim so imported or Source Code
// authored content survives the first keystroke.
const RAW_BLOCK_ATTRS = ['style', 'class', 'id'] as const;

// TextAlign owns text-align on the nodes it manages; left in the raw style it would
// win the merge and make setTextAlign a no-op.
function withoutTextAlign(style: string | null) {
	const rest = splitDeclarations(style || '')
		.map(part => part.trim())
		.filter(part => part && !/^text-align\s*:\s*(left|center|right|justify)\s*(!important)?\s*$/i.test(part))
		.join('; ');
	return rest ? `${rest};` : null;
}

function rawBlockAttributes(textAlignManaged = false) {
	const attributes: Record<string, any> = {};
	RAW_BLOCK_ATTRS.forEach(name => {
		attributes[name] = {
			default: null,
			parseHTML: (element: HTMLElement) =>
				name === 'style' && textAlignManaged
					? withoutTextAlign(element.getAttribute(name))
					: element.getAttribute(name),
			// The raw style never goes through tiptap's mergeAttributes (it splits on
			// `;` and `:`); blockAttributes() joins it with TextAlign's by hand.
			rendered: name !== 'style',
			renderHTML: (attrs: Record<string, any>) => (attrs[name] ? { [name]: attrs[name] } : {}),
		};
	});
	return attributes;
}

// Final attributes of a raw block node: TextAlign's style (the only other source of
// `style` on these nodes) first, then the raw style, joined by a space; then
// class/id and anything else.
function blockAttributes(
	node: { attrs: Record<string, any> },
	HTMLAttributes: Record<string, any>,
	base: Record<string, any> = {},
) {
	const { style: alignStyle, ...rest } = HTMLAttributes;
	const align = alignStyle ? String(alignStyle).trim() : '';
	const raw = node.attrs.style ? String(node.attrs.style) : '';
	const style = [align && raw && !align.endsWith(';') ? `${align};` : align, raw]
		.filter(Boolean)
		.join(' ');
	// Attributes the node renders itself (`start`) precede style; class/id follow it.
	const { class: cls, id, ...others } = rest;
	return {
		...others,
		...(style ? { style } : {}),
		...base,
		...(cls !== undefined ? { class: cls } : {}),
		...(id !== undefined ? { id } : {}),
	};
}

const BLOCK_CHILD_TAGS = new Set([
	'DIV', 'P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'UL', 'OL', 'BLOCKQUOTE', 'TABLE', 'PRE', 'FIGURE', 'HR',
	...RAW_BLOCK_TAGS.map(tag => tag.toUpperCase()),
]);

function hasBlockElementChild(element: Element) {
	return Array.from(element.children).some(child => BLOCK_CHILD_TAGS.has(child.tagName));
}

// A line is a <div>; a stored <p> keeps its tag (and its client margins).
const LinePar = Paragraph.extend({
	addAttributes() {
		return {
			...this.parent?.(),
			...rawBlockAttributes(true),
			tag: {
				default: 'div',
				parseHTML: (element: HTMLElement) => element.tagName.toLowerCase(),
				rendered: false,
			},
		};
	},
	parseHTML() {
		return [{ tag: 'div' }, { tag: 'p' }];
	},
	renderHTML({ node, HTMLAttributes }) {
		return elementWithRawAttributes(
			node.attrs.tag === 'p' ? 'p' : 'div',
			blockAttributes(node, HTMLAttributes, this.options.HTMLAttributes),
		);
	},
});

// A <div> wrapping block children (a styled container) keeps its wrapper instead of
// being flattened into an empty line plus siblings. Text-align stays in its raw style.
const DivBlock = TiptapNode.create({
	name: 'divBlock',
	group: 'block',
	content: 'block+',
	defining: true,
	addAttributes() {
		return rawBlockAttributes(false);
	},
	parseHTML() {
		return [
			{
				tag: 'div',
				priority: 60,
				getAttrs: (element: HTMLElement | string) =>
					typeof element !== 'string' && hasBlockElementChild(element) ? null : false,
			},
		];
	},
	renderHTML({ node, HTMLAttributes }) {
		return elementWithRawAttributes('div', blockAttributes(node, HTMLAttributes));
	},
});

const LegacyBulletList = BulletList.extend({
	addAttributes() {
		return { ...this.parent?.(), ...rawBlockAttributes() };
	},
	renderHTML({ node, HTMLAttributes }) {
		return elementWithRawAttributes('ul', blockAttributes(node, HTMLAttributes, this.options.HTMLAttributes));
	},
});

const LegacyOrderedList = OrderedList.extend({
	addAttributes() {
		return { ...this.parent?.(), ...rawBlockAttributes() };
	},
	renderHTML({ node, HTMLAttributes }) {
		// start="1" is the default; writing it would diff every untouched list.
		const { start, ...rest } = HTMLAttributes;
		return elementWithRawAttributes(
			'ol',
			blockAttributes(node, start === 1 ? rest : HTMLAttributes, this.options.HTMLAttributes),
		);
	},
});

const LegacyListItem = ListItem.extend({
	addAttributes() {
		return { ...this.parent?.(), ...rawBlockAttributes() };
	},
	renderHTML({ node, HTMLAttributes }) {
		return elementWithRawAttributes('li', blockAttributes(node, HTMLAttributes, this.options.HTMLAttributes));
	},
});

const HEADING_LEVELS = [1, 2, 3, 4, 5, 6];

// Hand-rolled: @tiptap/extension-heading is not a direct dependency of this package.
const LegacyHeading = TiptapNode.create({
	name: 'heading',
	group: 'block',
	content: 'inline*',
	defining: true,
	addAttributes() {
		return {
			level: { default: 1, rendered: false },
			...rawBlockAttributes(true),
		};
	},
	parseHTML() {
		return HEADING_LEVELS.map(level => ({ tag: `h${level}`, attrs: { level } }));
	},
	renderHTML({ node, HTMLAttributes }) {
		return elementWithRawAttributes(`h${node.attrs.level}`, blockAttributes(node, HTMLAttributes));
	},
});

// Hand-rolled: @tiptap/extension-blockquote is not a direct dependency of this package.
const LegacyBlockquote = TiptapNode.create({
	name: 'blockquote',
	group: 'block',
	content: 'block+',
	defining: true,
	addAttributes() {
		return rawBlockAttributes();
	},
	parseHTML() {
		return [{ tag: 'blockquote' }];
	},
	renderHTML({ node, HTMLAttributes }) {
		return elementWithRawAttributes('blockquote', blockAttributes(node, HTMLAttributes));
	},
});

// font-weight/font-style belong to Bold/Italic, so they are not "extra".
const MODELLED_CSS_PROPS = new Set([
	...TEXT_STYLE_PROPS.map(([, cssProp]) => cssProp),
	'font-weight',
	'font-style',
]);

function readOwnExtraStyle(element: HTMLElement) {
	return splitDeclarations(element.getAttribute('style') || '')
		.map(part => part.trim())
		.filter(part => part && !MODELLED_CSS_PROPS.has(part.split(':')[0].trim().toLowerCase()));
}

const declaredProp = (part: string) => part.split(':')[0].trim().toLowerCase();

// `background` vs `background-color`, `padding` vs `padding-left`, `font` vs
// `font-size`/`line-height`: either side being set nearer wins.
function overlaps(a: string, b: string) {
	if (a === b || a.startsWith(`${b}-`) || b.startsWith(`${a}-`)) return true;
	return (a === 'font' && b === 'line-height') || (b === 'font' && a === 'line-height');
}

function readExtraStyle(element: HTMLElement) {
	const extra = readOwnExtraStyle(element);
	// Everything set nearer, modelled props included, so an ancestor shorthand never
	// overrides a longhand the span (or a closer element) declares.
	const seen = new Set(
		splitDeclarations(element.getAttribute('style') || '')
			.map(declaredProp)
			.filter(Boolean),
	);
	const isSetNearer = (prop: string) => Array.from(seen).some(near => overlaps(prop, near));
	for (const ancestor of inheritanceChain(element)) {
		const declared = splitDeclarations(ancestor.getAttribute('style') || '')
			.map(part => part.trim())
			.filter(Boolean);
		// A mark tag keeps its own declarations; they only block the spans beyond it.
		if (ancestor.tagName === 'SPAN') {
			// Every declaration of this ancestor is kept (CSS uses the last of repeats);
			// `seen` only shuts out ancestors further away.
			readOwnExtraStyle(ancestor)
				.filter(part => !isSetNearer(declaredProp(part)))
				.forEach(part => extra.push(part));
		}
		declared.forEach(part => seen.add(declaredProp(part)));
	}
	return extra.length ? extra.join('; ') : null;
}

// One <span style> carries every inline property, like the legacy DOM surgery.
const InlineTextStyle = TextStyle.extend({
	addAttributes() {
		const attributes: Record<string, any> = {};
		TEXT_STYLE_PROPS.forEach(([name, cssProp]) => {
			attributes[name] = {
				default: null,
				parseHTML: (element: HTMLElement) => {
					const value = readInheritedStyleProp(element, cssProp);
					return name === 'fontSize' ? normalizeFontSize(value) : value;
				},
				rendered: false,
			};
		});
		[
			[DYNAMIC_COLOR_ATTR, 'color'],
			[DYNAMIC_BACKGROUND_COLOR_ATTR, 'background-color'],
		].forEach(([name, cssProp]) => {
			attributes[name] = {
				default: null,
				parseHTML: (element: HTMLElement) => readInheritedColorVar(element, cssProp, name),
			};
		});
		// Declarations outside the schema (vertical-align, padding...) kept verbatim.
		attributes.extraStyle = {
			default: null,
			parseHTML: (element: HTMLElement) => readExtraStyle(element),
			rendered: false,
		};
		// Rendered after style (and the data attributes).
		attributes.class = {
			default: null,
			parseHTML: (element: HTMLElement) => element.getAttribute('class'),
			renderHTML: (attrs: Record<string, any>) => (attrs.class ? { class: attrs.class } : {}),
		};
		return attributes;
	},
	// A span carrying only font-weight/font-style/line-through belongs to Bold/Italic/
	// Strike; claiming it too would leave an empty <span> around the <b>.
	parseHTML() {
		return [
			{
				tag: 'span',
				getAttrs: (element: HTMLElement | string) => {
					if (typeof element === 'string') return false;
					const owns =
						TEXT_STYLE_PROPS.some(([, cssProp]) => readStyleProp(element, cssProp)) ||
						readOwnExtraStyle(element).length > 0 ||
						element.hasAttribute(DYNAMIC_COLOR_ATTR) ||
						element.hasAttribute(DYNAMIC_BACKGROUND_COLOR_ATTR) ||
						// A merge tag badge is its own node, not a classed text span.
						(!!element.getAttribute('class') && !element.hasAttribute('data-tag-key'));
					return owns ? {} : false;
				},
			},
		];
	},
	renderHTML({ mark, HTMLAttributes }) {
		const style = [
			...TEXT_STYLE_PROPS.filter(([name]) => mark.attrs[name]).map(
				([name, cssProp]) => `${cssProp}: ${mark.attrs[name]}`,
			),
			...(mark.attrs.extraStyle ? [mark.attrs.extraStyle] : []),
		].join('; ');
		// style first, like the legacy surgery wrote it.
		return elementWithRawAttributes(
			'span',
			{ ...this.options.HTMLAttributes, ...(style ? { style } : {}), ...HTMLAttributes },
		);
	},
});

// Only the <u> tag: the legacy toolbar wrote underline as <u>, while a link's
// `text-decoration: underline` lives in its style attribute and must stay there.
const LegacyUnderline = Underline.extend({
	addAttributes() {
		return { ...this.parent?.(), ...markAttributes(['u']) };
	},
	renderHTML({ mark }) {
		return markElement('u', mark, this.options.HTMLAttributes);
	},
	parseHTML() {
		return [{ tag: 'u' }];
	},
});

const LINK_MODELLED_ATTRS = ['href', 'target', 'rel', 'class', 'style', 'tabindex'];

const InlineLink = Link.extend({
	addAttributes() {
		return {
			...this.parent?.(),
			// The canvas puts tabindex="-1" on every anchor; dropping it fails the gate.
			tabindex: { default: null },
			// No implicit target/rel/class: existing links re-serialise untouched.
			target: { default: null },
			rel: { default: null },
			class: { default: null },
			style: {
				default: null,
				parseHTML: (element: HTMLElement) => element.getAttribute('style'),
				renderHTML: (attrs: Record<string, any>) =>
					attrs.style ? { style: attrs.style } : {},
			},
			// Every other attribute (title, id, name, aria-*, data-*) in original order.
			extraAttrs: {
				default: null,
				parseHTML: (element: HTMLElement) => {
					const extra: Record<string, string> = {};
					Array.from(element.attributes).forEach(({ name, value }) => {
						if (!LINK_MODELLED_ATTRS.includes(name)) extra[name] = value;
					});
					return Object.keys(extra).length ? extra : null;
				},
				renderHTML: (attrs: Record<string, any>) => attrs.extraAttrs || {},
			},
		};
	},
	// Attribute order href, target, rel, style as the legacy createLink wrote it;
	// the extension's option-level defaults (target/rel) are deliberately skipped.
	renderHTML({ HTMLAttributes }) {
		return elementWithRawAttributes('a', HTMLAttributes);
	},
});

const LegacyHorizontalRule = HorizontalRule.extend({
	addAttributes() {
		return { ...this.parent?.(), ...rawBlockAttributes() };
	},
	renderHTML({ node, HTMLAttributes }) {
		const { dom } = elementWithRawAttributes('hr', blockAttributes(node, HTMLAttributes, this.options.HTMLAttributes));
		return { dom };
	},
});

// ProseMirror's own editor rules; its stylesheet is injected into document.head and
// never reaches the shadow root the canvas renders in.
export const EDITOR_ROOT_STYLE =
	'outline: none; white-space: pre-wrap; word-wrap: break-word;';

// Pasted text is plain, but its line separators must become schema breaks or the
// saved email carries a literal newline that collapses to a space.
export function plainTextToFragment(schema: Schema, text: string) {
	const nodes: any[] = [];
	text.split(/\r\n|\r|\n/).forEach((line, i) => {
		if (i > 0) nodes.push(schema.nodes.hardBreak.create());
		if (line) nodes.push(schema.text(line));
	});
	return Fragment.fromArray(nodes);
}

export function buildTextBlockExtensions(): AnyExtension[] {
	return [
		Document,
		Text,
		DivBlock,
		LinePar,
		HardBreak,
		LegacyBold,
		LegacyItalic,
		LegacyUnderline,
		LegacyStrike,
		InlineTextStyle,
		// Existing links must re-serialise untouched, so no implicit target/rel;
		// the createLink command supplies them.
		InlineLink.configure({
			openOnClick: false,
			autolink: false,
			linkOnPaste: false,
			HTMLAttributes: {},
		}),
		TextAlign.configure({ types: ['paragraph', 'heading'], defaultAlignment: null as any }),
		LegacyHeading,
		LegacyBlockquote,
		LegacyBulletList,
		LegacyOrderedList,
		LegacyListItem,
		LegacyHorizontalRule,
		History,
		MergeTagNode,
		RawBlock,
		RawInline,
	];
}

// Chrome's contenteditable leaves an empty line as <div><br></div>; ProseMirror
// models that as an empty paragraph, so drop the placeholder break on the way in
// (toLegacyHtml puts it back).
//
// MJML formats the rendered block with newlines and indentation around and between
// the line <div>s. With `preserveWhitespace: 'full'` each of those whitespace-only
// text nodes would become its own paragraph (a blank line above the text), so drop
// them at the edges and beside block elements. Whitespace inside a text run stays.
const BLOCK_TAGS = new Set([
	'DIV', 'P', 'UL', 'OL', 'HR', 'TABLE', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'PRE',
]);

// The Text block render (bay-email-core Text) wraps content in this div for its
// border; stored content never holds it, so strip it before editing or comparing.
const BORDER_WRAP = /^\s*<div class="bay-text-border-wrap"[^>]*>([\s\S]*)<\/div>\s*$/i;

// Render-only <div style="white-space:nowrap;"> the Text block adds inside the border wrapper.
const WHITE_SPACE_WRAP =
	/^(?:<(div|span)\s+style=["']\s*white-space\s*:\s*(?:normal|nowrap)\s*;?\s*["']>([\s\S]*)<\/\1>)+$/i;

export function unwrapBorderWrap(html: string) {
	const match = html.match(BORDER_WRAP);
	const inner = match ? match[1] : html;
	// Same shape the core strips: nested white-space wrappers around the whole string.
	return inner.replace(WHITE_SPACE_WRAP, (_, __, content) => content);
}

const FONT_SIZES: Record<string, string> = {
	'1': '10px', '2': '13px', '3': '16px', '4': '18px', '5': '24px', '6': '32px', '7': '48px',
};

export function normalizeLegacyInput(html: string) {
	const container = document.createElement('div');
	container.innerHTML = stripRenderWhitespace(html);
	// execCommand's <font> becomes the styled span InlineTextStyle models.
	container.querySelectorAll('font').forEach(font => {
		if (font.closest(RAW_ISLAND_SELECTOR)) return;
		// The inline style beats the presentational attributes, so it comes first.
		const existing = (font.getAttribute('style') || '').trim().replace(/;$/, '');
		const parts: string[] = existing ? [existing] : [];
		const color = font.getAttribute('color');
		if (color) parts.push(`color: ${color}`);
		const size = FONT_SIZES[font.getAttribute('size') || ''];
		if (size) parts.push(`font-size: ${size}`);
		const face = font.getAttribute('face');
		if (face) parts.push(`font-family: ${face}`);
		const span = document.createElement('span');
		if (parts.length) span.setAttribute('style', `${parts.join('; ')};`);
		span.append(...Array.from(font.childNodes));
		font.replaceWith(span);
	});
	container.querySelectorAll('div').forEach(line => {
		if (line.closest(RAW_ISLAND_SELECTOR)) return;
		if (line.childNodes.length === 1 && line.firstChild?.nodeName === 'BR') {
			line.firstChild.remove();
		}
	});
	return container.innerHTML;
}

// The whitespace half of normalizeLegacyInput on its own: the reference shape
// a round trip is measured against (the <br> placeholder is restored on output,
// so it must stay in the reference).
export function stripRenderWhitespace(html: string) {
	const container = document.createElement('div');
	container.innerHTML = html;
	Array.from(container.childNodes).forEach(node => {
		if (node.nodeType !== Node.TEXT_NODE) return;
		const prev = node.previousSibling;
		const next = node.nextSibling;
		const atStart = !prev || BLOCK_TAGS.has(prev.nodeName);
		const atEnd = !next || BLOCK_TAGS.has(next.nodeName);
		let text = node.textContent || '';
		// Plain whitespace only — &nbsp; (\u00a0) is content the user typed.
		if (atStart) text = text.replace(/^[ \t\r\n]+/, '');
		if (atEnd) text = text.replace(/[ \t\r\n]+$/, '');
		if (!text) node.remove();
		else if (text !== node.textContent) node.textContent = text;
	});
	return container.innerHTML;
}

// Pure string form of toLegacyHtml, so it is testable without a mounted editor.
export function toLegacyHtmlString(editorHtml: string, mountHtml: string) {
	const container = document.createElement('div');
	container.innerHTML = editorHtml;

	// Raw islands (tables, images, locked spans...) are verbatim; no pass touches them.
	const isRaw = (el: Element) => !!el.closest(RAW_ISLAND_SELECTOR);

	// Empty line: a bare <div></div> or <p></p> collapses in email clients.
	container.querySelectorAll('div, p').forEach(line => {
		if (isRaw(line)) return;
		if (line.childNodes.length === 0) line.appendChild(document.createElement('br'));
	});

	// <li><div>text</div></li> -> <li>text</li>
	container.querySelectorAll('li').forEach(item => {
		if (isRaw(item)) return;
		const only = item.childNodes.length === 1 ? item.firstElementChild : null;
		if (only?.tagName === 'DIV' && only.attributes.length === 0 && !hasBlockElementChild(only)) {
			only.replaceWith(...Array.from(only.childNodes));
		}
	});

	// Match the browser's own style serialisation: declarations end with ';'.
	container.querySelectorAll('[style]').forEach(el => {
		if (isRaw(el)) return;
		const style = (el.getAttribute('style') || '').trim();
		if (style && !style.endsWith(';')) el.setAttribute('style', `${style};`);
	});

	// Legacy shape: bare first line, a <div> per following line.
	const first = container.firstChild;
	if (
		first instanceof HTMLElement &&
		first.tagName === 'DIV' &&
		first.attributes.length === 0 &&
		!hasBlockElementChild(first) &&
		!/^\s*<div/i.test(mountHtml)
	) {
		// An empty first line is represented by nothing, not a bare <br>.
		const isPlaceholder =
			first.childNodes.length === 1 && first.firstChild?.nodeName === 'BR';
		if (isPlaceholder && container.childNodes.length === 1) {
			first.remove();
		} else {
			first.replaceWith(...Array.from(first.childNodes));
		}
	}

	return container.innerHTML;
}

export function toLegacyHtml(editor: Editor, mountHtml: string) {
	return toLegacyHtmlString(editor.getHTML(), mountHtml);
}
